import { styled } from '@mui/material/styles';
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowId,
    gridClasses,
    gridFilteredSortedRowIdsSelector,
    useGridApiRef,
} from '@mui/x-data-grid';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { openReportAPIRefSliceAction } from '../../../../app/Slices/ReportAPIRefSlice';
import UserRole from '../../../../shared/AuthRoles';
import CustomNoRowsOverlay from '../../../shared/NoRowsOverlayCustom';

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .MuiTablePagination-toolbar': {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignContent: 'center',
    },
    '& .MuiTablePagination-displayedRows ': {
        paddingTop: '14px',
    },
    '& .MuiTablePagination-selectLabel': {
        paddingTop: '14px',
    },
    '& .MuiTablePagination-select': {
        paddingTop: '4px',
    },
    '& .MuiDataGrid-columnHeader': {
        fontSize: '12px',
        fontWeight: 'bold',
        color: '#000',
        borderBottom: `0px solid ${theme.palette.midGrey.main}`,
        paddingLeft: '0px',
    },
    [`& .${gridClasses.row}`]: {
        '&:hover, &.Mui-hovered': {
            backgroundColor: 'none',
            color: theme.palette.primary.main,
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: `${theme.palette.neutral.main}`,
            color: theme.palette.primary.main,
            '&:hover, &.Mui-hovered': {
                backgroundColor: 'none',
                color: theme.palette.primary.main,
                '@media (hover: none)': {
                    backgroundColor: 'none',
                },
            },
        },
    },
}));

export default function AIHistoryTable({
    conversations,
}: {
    conversations: any;
}) {
    const loggedInUser = UserRole();

    const apiRef = useGridApiRef();
    const dispatch = useDispatch();

    const [data, setData] = useState<GridRowId[]>();

    useEffect(() => {
        const csv = apiRef?.current.exportDataAsCsv;
        const print = apiRef?.current.exportDataAsPrint;
        dispatch(
            openReportAPIRefSliceAction({
                exportInfo: {
                    csvExport: csv,
                    printExport: print,
                    search: apiRef?.current.setQuickFilterValues,
                },
            }),
        );
    }, [apiRef, dispatch]);

    const renderLinkCell = (params: GridRenderCellParams) => (
        <Link
            to={{
                pathname: `/ai-history/${params.row.idInDb}`,
            }}
            state={{
                name: 'viewReservation',
                component: 'reservations',
                url: 'reservations',
                index: data?.findIndex((value) => value === params.id),
                reservations: data,
            }}
            style={{
                textDecoration: 'none',
                color: 'inherit',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            {params.value}
        </Link>
    );

    const columns: GridColDef[] = [
        {
            field: 'idInDb',
            headerName: 'Ref#',
            type: 'string',
            width: 100,
            minWidth: 100,
            flex: 1,
            sortable: true,
            renderCell: renderLinkCell,
        },
        {
            field: 'createdAt',
            headerName: 'Date',
            type: 'string',
            width: 150,
            minWidth: 150,
            flex: 1,
            sortable: true,
            renderCell: (params) =>
                renderLinkCell({
                    ...params,
                    value: moment(params?.value).format('ll'),
                }),
        },
        {
            field: 'user',
            headerName: 'User',
            type: 'string',
            width: 230,
            minWidth: 230,
            flex: 1,
            sortable: true,
            valueGetter: (params) => {
                return params?.row.user?.email;
            },
            renderCell: renderLinkCell,
        },
        {
            field: 'company',
            headerName: 'Company',
            type: 'string',
            width: 200,
            minWidth: 200,
            flex: 1,
            sortable: true,
            renderCell: renderLinkCell,
        },
        {
            field: 'reservation ref',
            headerName: 'Reservation Ref.',
            type: 'string',
            width: 200,
            minWidth: 200,
            flex: 1,
            sortable: true,
            valueGetter: (params) => {
                return params?.row.booking?.id ? params?.row.booking?.id : '-';
            },
            renderCell: renderLinkCell,
        },
    ];

    const handleStateChange = () => {
        setData(gridFilteredSortedRowIdsSelector(apiRef));
    };

    return (
        <StripedDataGrid
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            onStateChange={handleStateChange}
            sx={{
                paddingTop: '30px',
                boxShadow: 0,
                border: 0,
                '& .MuiDataGrid-cell': {
                    padding: '0',
                },
            }}
            apiRef={apiRef}
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        owner:
                            loggedInUser?.role === 'guest_admin' ||
                            loggedInUser?.role === 'guest_department_admin' ||
                            loggedInUser?.role === 'guest_user',
                        recurringId: loggedInUser?.role.includes('owner')
                            ? true
                            : false,
                    },
                },
                pagination: {
                    paginationModel: {
                        pageSize: 20,
                    },
                },

                sorting: {
                    sortModel: [
                        {
                            field: 'idInDb',
                            sort: 'desc',
                        },
                    ],
                },
            }}
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            rows={conversations !== undefined ? conversations : []}
            columns={columns}
            slots={{
                noRowsOverlay: CustomNoRowsOverlay,
            }}
        />
    );
}
